import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Stack,
  Chip,
  Avatar,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  TrendingUp,
  Email,
  Analytics,
  Gavel,
  CheckCircle,
  ExpandMore,
  ArrowForward,
  Business,
  SmartToy,
  ContactSupport,
} from '@mui/icons-material';

const LandingPage: React.FC = () => {
  const features = [
    {
      icon: <SmartToy />,
      title: 'AI CRM',
      description: '顧客管理・会話履歴の自動記録と分析。感情分析でより深い顧客理解を実現。',
    },
    {
      icon: <Analytics />,
      title: 'AI Intelligence Suite',
      description: '顧客・競合企業の情報収集・分析を自動化。Web上の最新情報を瞬時に収集・レポート化。',
    },
    {
      icon: <Gavel />,
      title: 'リーガルチェック',
      description: '契約書の自動レビュー・リスク分析。法務担当者の工数を大幅削減。',
    },
    {
      icon: <Email />,
      title: 'AIメール作成',
      description: '9種類の日本語ビジネスメール自動生成。顧客別にカスタマイズされた文面を作成。',
    },
    {
      icon: <TrendingUp />,
      title: 'ワークフロー管理',
      description: 'AI改善提案付き業務プロセス最適化。無駄な作業を特定し効率化を提案。',
    },
    {
      icon: <Business />,
      title: 'マーケティングAI',
      description: '顧客分析・競合分析・キャンペーン提案を自動生成。戦略立案をAIがサポート。',
    },
  ];

  const benefits = [
    { title: '業務時間', before: '8時間/日', after: '3時間/日', improvement: '62%削減' },
    { title: 'メール作成時間', before: '20分/通', after: '2分/通', improvement: '90%削減' },
    { title: '契約書レビュー', before: '2時間/件', after: '5分/件', improvement: '95%削減' },
    { title: '情報収集・分析', before: '1時間/社', after: '5分/社', improvement: '92%削減' },
  ];

  const pricingPlans = [
    {
      name: 'スタータープラン',
      price: 'お問い合わせ',
      description: '小規模企業向けエントリーパッケージ',
      features: [
        'AI CRM（顧客数10件まで）',
        'AIメール作成（月300通まで）',
        'ベーシックリーガルチェック',
        'メールサポート',
        '標準導入パッケージ',
      ],
      recommended: false,
    },
    {
      name: 'プロフェッショナルプラン',
      price: 'お問い合わせ',
      description: '中規模企業向け総合ソリューション',
      features: [
        'AI CRM（顧客数200件まで）',
        '主要AI機能利用',
        'AI Intelligence Suite（月500件まで）',
        'ベーシック連携サポート',
        'メール・電話サポート',
        '導入コンサルティング',
      ],
      recommended: true,
    },
    {
      name: 'エンタープライズプラン',
      price: 'お問い合わせ',
      description: 'お客様のニーズに合わせたカスタマイズ導入',
      features: [
        'AI CRM（顧客数無制限）',
        '全AI機能フル利用・カスタマイズ',
        'AI Intelligence Suite（無制限）',
        'ワークフロー最適化・設計支援',
        '既存システム連携対応',
        '専任サポート・コンサルティング',
        'セキュリティ監査対応',
        'オンサイト導入支援',
      ],
      recommended: false,
    },
  ];

  const testimonials = [
    {
      name: '田中 太郎',
      company: '株式会社ABC商事',
      role: '代表取締役',
      comment: '業務効率が劇的に向上しました。AIが複雑な分析をしてくれるので、戦略立案に集中できるようになりました。',
      result: '業務効率70%向上',
    },
    {
      name: '佐藤 花子',
      company: 'XYZ法律事務所',
      role: '弁護士',
      comment: '契約書レビューの時間が大幅に短縮され、より多くの案件に対応できるようになりました。',
      result: 'レビュー時間95%削減',
    },
    {
      name: '山田 次郎',
      company: 'DEFコンサルティング',
      role: '経営コンサルタント',
      comment: 'AIが情報収集や分析をしてくれるので、クライアントへの価値提供に時間を使えます。',
      result: '情報収集効率98%向上',
    },
  ];

  const faqs = [
    {
      question: 'bitgrit OSとは何ですか？',
      answer: 'bitgrit OSは、AI技術を活用して様々な業務を自動化・効率化するクラウドサービスです。手作業による時間のかかる業務をAIが代行し、人間はより創造的で戦略的な仕事に集中できるようになります。',
    },
    {
      question: '導入にはどのくらいの時間がかかりますか？',
      answer: '現在、様々なお客様のニーズに対応するため、お客様のニーズを詳しくヒアリングしてからのカスタマイズ導入を積極的に行っております。そのため、導入には1-2週間程度お時間をいただく場合がございます。',
    },
    {
      question: 'セキュリティは大丈夫ですか？',
      answer: 'はい。エンタープライズレベルのセキュリティを提供しており、データは暗号化され、ISO27001準拠のデータセンターで管理されています。マルチテナント対応により、他社のデータと完全に分離されています。',
    },
    {
      question: '既存のCRMシステムとの連携は可能ですか？',
      answer: '既存システムとの連携につきましては、順次対応を進めております。お客様がご利用のシステムについて、まずはお気軽にお問い合わせください。',
    },
    {
      question: 'サポート体制はどうなっていますか？',
      answer: 'プランに応じて、メールサポート、電話サポート、専任サポートを提供しています。また、導入コンサルティングやオンサイト支援も行っており、お客様のビジネス成功を全力でサポートいたします。',
    },
    {
      question: '料金について詳しく教えてください。',
      answer: 'お客様のニーズや導入規模に応じて最適なプランをご提案させていただいております。まずはお問い合わせいただき、ご要件をお聞かせください。お客様に最適なソリューションと料金をご提案いたします。',
    },
  ];

  return (
    <Box>
      {/* ヒーローセクション */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #6F389F 0%, #8B5CF6 25%, #A855F7 50%, #C084FC 75%, #DDD6FE 100%)',
          color: 'white',
          py: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 20% 80%, rgba(167, 139, 250, 0.3) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(196, 181, 253, 0.2) 0%, transparent 50%)',
            pointerEvents: 'none',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: '-30%',
            right: '-10%',
            width: '40%',
            height: '120%',
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '50%',
            transform: 'rotate(-15deg)',
            pointerEvents: 'none',
          },
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Stack spacing={4}>
                <Typography variant="h1" sx={{ fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
                  bitgrit OS<br />
                  AIで業務を、人間は創造性を
                </Typography>
                <Typography variant="h4" sx={{ opacity: 0.9, fontWeight: 400 }}>
                  あらゆる業務の手作業をAIが代行。<br />
                  あなたは戦略的思考に集中できます。
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Button
                    variant="contained"
                    size="large"
                    sx={{
                      bgcolor: 'white',
                      color: 'primary.main',
                      '&:hover': { bgcolor: 'grey.100' },
                      px: 4,
                      py: 2,
                      fontSize: '1.2rem',
                    }}
                    endIcon={<ArrowForward />}
                  >
                    お問い合わせ・相談
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    sx={{
                      borderColor: 'white',
                      color: 'white',
                      '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
                      px: 4,
                      py: 2,
                      fontSize: '1.1rem',
                    }}
                  >
                    サービス詳細
                  </Button>
                </Stack>
                <Stack direction="row" spacing={3} sx={{ mt: 4 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>70%</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>業務時間削減</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>97%</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>メール作成効率化</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>95%</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>レビュー時間短縮</Typography>
                  </Box>
                </Stack>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  height: 400,
                  background: 'rgba(255,255,255,0.1)',
                  borderRadius: 4,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <Typography variant="h6" sx={{ opacity: 0.8 }}>
                  AI + Human Collaboration Demo
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* 問題提起セクション */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography variant="h2" gutterBottom>
            こんな課題を抱えていませんか？
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
            多くの企業が日常業務で直面している課題
          </Typography>
        </Box>
        <Grid container spacing={4}>
          {[
            { title: '業務に時間がかかりすぎる', description: '情報収集、メール作成、資料作成で1日が終わってしまう' },
            { title: '属人化によるリスク', description: '特定の人に依存した業務で、ノウハウが蓄積されない' },
            { title: '単純作業の繰り返し', description: '創造性を必要としない作業に貴重な時間を奪われている' },
            { title: '一貫性のない対応', description: '担当者によって対応品質にばらつきが生じている' },
          ].map((item, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Paper
                elevation={2}
                sx={{
                  p: 4,
                  height: '100%',
                  borderLeft: 4,
                  borderColor: 'error.main',
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'translateY(-4px)' },
                }}
              >
                <Typography variant="h5" gutterBottom color="error.main">
                  {item.title}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {item.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* 解決策セクション */}
      <Box sx={{ 
        background: 'linear-gradient(180deg, #F8F6FF 0%, #FEFCFF 50%, #F5F3FF 100%)',
        py: { xs: 8, md: 12 },
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '80px',
          background: 'linear-gradient(180deg, rgba(111, 56, 159, 0.03) 0%, transparent 100%)',
          borderRadius: '0 0 100% 100%',
          transform: 'scaleX(1.5)',
        },
      }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Chip
              label="AI-Powered Solution"
              color="primary"
              sx={{ mb: 2, px: 2, py: 1, fontSize: '0.9rem' }}
            />
            <Typography variant="h2" gutterBottom>
              AIがあなたの業務を完全自動化
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
              6つのAI機能で、あらゆる業務を効率化
            </Typography>
          </Box>
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  elevation={3}
                  sx={{
                    height: '100%',
                    transition: 'all 0.3s',
                    borderRadius: 4,
                    background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
                    border: '1px solid rgba(111, 56, 159, 0.08)',
                    '&:hover': {
                      transform: 'translateY(-12px) scale(1.02)',
                      boxShadow: '0 20px 40px rgba(111, 56, 159, 0.15)',
                      background: 'linear-gradient(145deg, #ffffff 0%, #faf5ff 100%)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 64,
                        height: 64,
                        borderRadius: 2,
                        bgcolor: 'primary.main',
                        color: 'white',
                        mb: 3,
                        fontSize: '2rem',
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography variant="h5" gutterBottom>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ベネフィットセクション */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography variant="h2" gutterBottom>
            導入効果の実測値
          </Typography>
          <Typography variant="body1" color="text.secondary">
            実際の導入企業での効果測定結果
          </Typography>
        </Box>
        <Grid container spacing={4}>
          {benefits.map((benefit, index) => (
            <Grid item xs={12} md={6} lg={3} key={index}>
              <Paper
                elevation={2}
                sx={{
                  p: 4,
                  textAlign: 'center',
                  height: '100%',
                  background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 50%, #f8fafc 100%)',
                  borderRadius: 3,
                  border: '1px solid rgba(111, 56, 159, 0.1)',
                  boxShadow: '0 4px 20px rgba(111, 56, 159, 0.08)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 30px rgba(111, 56, 159, 0.12)',
                  },
                }}
              >
                <Typography variant="h6" gutterBottom>
                  {benefit.title}
                </Typography>
                <Stack spacing={2} sx={{ my: 3 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">導入前</Typography>
                    <Typography variant="h5" color="error.main">{benefit.before}</Typography>
                  </Box>
                  <ArrowForward sx={{ mx: 'auto', color: 'primary.main' }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary">導入後</Typography>
                    <Typography variant="h5" color="success.main">{benefit.after}</Typography>
                  </Box>
                </Stack>
                <Chip
                  label={benefit.improvement}
                  color="success"
                  variant="filled"
                  sx={{ fontWeight: 600 }}
                />
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* 料金プラン */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #E5E7EB 0%, #F3F4F6 25%, #FEFCFF 50%, #F8F6FF 75%, #EDE9FE 100%)',
        py: { xs: 8, md: 12 },
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '-50px',
          left: 0,
          right: 0,
          height: '100px',
          background: 'linear-gradient(180deg, rgba(255,255,255,0.7) 0%, transparent 100%)',
          borderRadius: '50%',
          transform: 'scaleX(2)',
        },
      }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="h2" gutterBottom>
              カスタマイズプラン
            </Typography>
            <Typography variant="body1" color="text.secondary">
              お客様のニーズに合わせた最適なソリューションをご提案
            </Typography>
          </Box>
          <Grid container spacing={4} justifyContent="center">
            {pricingPlans.map((plan, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  elevation={plan.recommended ? 8 : 2}
                  sx={{
                    height: '100%',
                    position: 'relative',
                    border: plan.recommended ? 2 : 0,
                    borderColor: 'primary.main',
                    transform: plan.recommended ? 'scale(1.05)' : 'none',
                  }}
                >
                  {plan.recommended && (
                    <Chip
                      label="おすすめ"
                      color="primary"
                      sx={{
                        position: 'absolute',
                        top: -12,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        fontWeight: 600,
                      }}
                    />
                  )}
                  <CardContent sx={{ p: 4 }}>
                    <Typography variant="h4" gutterBottom textAlign="center">
                      {plan.name}
                    </Typography>
                    <Typography
                      variant="h3"
                      color="primary.main"
                      textAlign="center"
                      sx={{ mb: 2, fontWeight: 700 }}
                    >
                      {plan.price}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 4 }}>
                      {plan.description}
                    </Typography>
                    <Stack spacing={2} sx={{ mb: 4 }}>
                      {plan.features.map((feature, featureIndex) => (
                        <Box key={featureIndex} sx={{ display: 'flex', alignItems: 'center' }}>
                          <CheckCircle color="success" sx={{ mr: 2, fontSize: '1.2rem' }} />
                          <Typography variant="body2">{feature}</Typography>
                        </Box>
                      ))}
                    </Stack>
                    <Button
                      variant={plan.recommended ? 'contained' : 'outlined'}
                      fullWidth
                      size="large"
                      sx={{ py: 2 }}
                      startIcon={<ContactSupport />}
                    >
                      お問い合わせ
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* お客様の声 */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography variant="h2" gutterBottom>
            お客様の声
          </Typography>
          <Typography variant="body1" color="text.secondary">
            実際にbitgrit OSを導入した企業様の声
          </Typography>
        </Box>
        <Grid container spacing={4}>
          {testimonials.map((testimonial, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card elevation={2} sx={{ height: '100%' }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="body1" sx={{ mb: 3, fontStyle: 'italic' }}>
                    「{testimonial.comment}」
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                      {testimonial.name[0]}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {testimonial.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {testimonial.company} {testimonial.role}
                      </Typography>
                    </Box>
                  </Box>
                  <Chip
                    label={testimonial.result}
                    color="success"
                    variant="outlined"
                    size="small"
                  />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* FAQ */}
      <Box sx={{ 
        background: 'linear-gradient(45deg, #F1F5F9 0%, #F8FAFC 30%, #FEFCFF 60%, #F5F3FF 100%)',
        py: { xs: 8, md: 12 },
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '-30px',
          left: 0,
          right: 0,
          height: '60px',
          background: 'linear-gradient(180deg, rgba(111, 56, 159, 0.02) 0%, transparent 100%)',
          borderRadius: '100%',
          transform: 'scaleX(3)',
        },
      }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="h2" gutterBottom>
              よくある質問
            </Typography>
            <Typography variant="body1" color="text.secondary">
              bitgrit OSについてのご質問にお答えします
            </Typography>
          </Box>
          <Stack spacing={2}>
            {faqs.map((faq, index) => (
              <Accordion key={index} elevation={2}>
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  sx={{ py: 2 }}
                >
                  <Typography variant="h6">{faq.question}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body1">{faq.answer}</Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* 最終CTA */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #38115A 0%, #6F389F 25%, #8B5CF6 50%, #A855F7 75%, #6F389F 100%)',
          color: 'white',
          py: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 70% 30%, rgba(167, 139, 250, 0.2) 0%, transparent 50%), radial-gradient(circle at 30% 70%, rgba(196, 181, 253, 0.15) 0%, transparent 50%)',
            pointerEvents: 'none',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: '-20%',
            left: '-10%',
            width: '50%',
            height: '60%',
            background: 'rgba(255, 255, 255, 0.03)',
            borderRadius: '50%',
            transform: 'rotate(25deg)',
            pointerEvents: 'none',
          },
        }}
      >
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography variant="h2" gutterBottom>
            今すぐAIで業務を自動化しませんか？
          </Typography>
          <Typography variant="h5" sx={{ mb: 4, opacity: 0.9, fontWeight: 400 }}>
            お客様に最適なソリューションをご提案いたします
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              sx={{
                bgcolor: 'white',
                color: 'primary.main',
                '&:hover': { bgcolor: 'grey.100' },
                px: 6,
                py: 2,
                fontSize: '1.2rem',
              }}
              endIcon={<ContactSupport />}
            >
              お問い合わせ・相談
            </Button>
            <Button
              variant="outlined"
              size="large"
              sx={{
                borderColor: 'white',
                color: 'white',
                '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
                px: 6,
                py: 2,
                fontSize: '1.1rem',
              }}
            >
              資料をダウンロード
            </Button>
          </Stack>
          <Typography variant="body2" sx={{ mt: 4, opacity: 0.8 }}>
            * お客様のニーズに合わせたカスタマイズ対応 * 専任サポート付き * 導入コンサルティング無料
          </Typography>
        </Container>
      </Box>

      {/* フッター */}
      <Box sx={{ bgcolor: 'grey.900', color: 'white', py: 6 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Typography variant="h5" gutterBottom>
                bitgrit OS
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                AIで業務を、人間は創造性を。<br />
                あらゆる業務の手作業をAIが代行します。
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom>
                製品
              </Typography>
              <Stack spacing={1}>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>AI CRM</Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>AI Intelligence Suite</Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>リーガルチェック</Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>AIメール作成</Typography>
              </Stack>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom>
                サポート
              </Typography>
              <Stack spacing={1}>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>お問い合わせ</Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>ヘルプセンター</Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>利用規約</Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>プライバシーポリシー</Typography>
              </Stack>
            </Grid>
          </Grid>
          <Box sx={{ textAlign: 'center', mt: 4, pt: 4, borderTop: 1, borderColor: 'grey.700' }}>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              © 2025 bitgrit Inc. All rights reserved.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;